import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useLead, useSettings } from "../lib/store";
import { postInquiry } from "../lib/api";
import { track } from "../lib/track";
import { waLink, MSG } from "../lib/whatsapp";
import { WhatsAppIcon } from "./icons";

const TRIP_TYPES = ["Private Trip", "Group Trip", "Family Trip", "Honeymoon", "Corporate", "Study Tour", "Halal Trip"];

export default function LeadModal() {
  const { lead, closeLead, isOpen } = useLead();
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", whatsapp: "", destination: "", date: "", pax: "", tripType: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({ name: "", whatsapp: "", destination: lead.destination || "", date: "", pax: "", tripType: "" });
    }
  }, [lead?._t]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim()) {
      toast.error("Mohon isi nama dan nomor WhatsApp Anda.");
      return;
    }
    setSending(true);
    try {
      await postInquiry({ type: "lead", ...form, source: lead?.source || window.location.pathname });
      track("click_whatsapp", { destination: form.destination, source: "lead_modal" });
      window.open(waLink(settings, MSG.lead(form)), "_blank");
      toast.success("Terima kasih! Kami membuka WhatsApp untuk melanjutkan konsultasi Anda.");
      closeLead();
    } catch {
      toast.error("Gagal mengirim data. Coba lagi atau lanjut langsung ke WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const skip = () => {
    track("click_whatsapp", { source: "lead_modal_skip" });
    window.open(waLink(settings, MSG.general), "_blank");
    closeLead();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end justify-center bg-charcoal/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={closeLead}
          data-testid="lead-modal"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-t-3xl bg-white p-6 shadow-elevated sm:rounded-3xl sm:p-8"
          >
            <button onClick={closeLead} aria-label="Tutup" data-testid="lead-modal-close" className="absolute right-4 top-4 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-charcoal">
              <X className="h-5 w-5" />
            </button>

            <p className="eyebrow">Konsultasi Gratis</p>
            <h3 className="mt-1 font-heading text-2xl font-bold text-charcoal">
              Konsultasi via <span className="text-tosca-600">WhatsApp</span>
            </h3>
            <p className="mt-1 text-sm text-slate-500">Isi singkat, tim kami langsung siap bantu rencana perjalanan Anda.</p>

            <form onSubmit={submit} className="mt-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Nama*</label>
                  <input className="field" placeholder="Nama Anda" value={form.name} onChange={set("name")} data-testid="lead-modal-name-input" />
                </div>
                <div>
                  <label className="label">No. WhatsApp*</label>
                  <input className="field" placeholder="08xxxxxxxxxx" value={form.whatsapp} onChange={set("whatsapp")} data-testid="lead-modal-whatsapp-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Destinasi</label>
                  <input className="field" placeholder="cth. China" value={form.destination} onChange={set("destination")} data-testid="lead-modal-destination-input" />
                </div>
                <div>
                  <label className="label">Tanggal</label>
                  <input type="date" className="field" value={form.date} onChange={set("date")} data-testid="lead-modal-date-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Jumlah Orang</label>
                  <input className="field" placeholder="cth. 4" value={form.pax} onChange={set("pax")} data-testid="lead-modal-pax-input" />
                </div>
                <div>
                  <label className="label">Tipe Trip</label>
                  <select className="field" value={form.tripType} onChange={set("tripType")} data-testid="lead-modal-trip-type-select">
                    <option value="">Pilih tipe trip</option>
                    {TRIP_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="submit" disabled={sending} className="btn btn-wa w-full" data-testid="lead-modal-submit">
                <WhatsAppIcon className="h-5 w-5" /> {sending ? "Menghubungkan..." : "Lanjut ke WhatsApp"}
              </button>
              <button type="button" onClick={skip} className="w-full text-center text-xs font-medium text-slate-400 underline underline-offset-2 transition hover:text-tosca-600" data-testid="lead-modal-skip">
                Lewati, chat WhatsApp langsung saja
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
